/**
 * COCHRANE-WEST — roster wave entries. GENERATED BY ASSEMBLY, WRITTEN BY REVIEW.
 *
 * Geography (streets, landmarks, coordinates, adjacency) ported from the source
 * roster; airspace derived by the calibrated classifier in
 * scripts/derive-airspace.mjs; every descriptive sentence written fresh from
 * aerial-media truth and fact-checked against its generation brief. See
 * docs/geo/VISION-v3.md for the fact contract these entries were written under.
 *
 * 3 entries. Edit by hand freely — assembly never overwrites a
 * file that has drifted from its fragments without --force.
 */
import type { Location } from "../types";

export const ROSTER_COCHRANE_WEST: Location[] = [
// ─────────────────────────────────────────────────────────────── BOTTREL ──
  {
    slug: "bottrel",
    name: "Bottrel",
    region: "cochrane-west",
    city: "Rocky View County",
    province: "Alberta",
    coordinates: { lat: 51.4167, lng: -114.5167 },
    tier: 4,
    driveMinutesFromCalgary: 50,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 36,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 36 km from Springbank Airport (CYBW), Bottrel lies outside the charted Calgary-area control zones, and no authorisation is normally required. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Evening light leaves Grand Valley from the floor up — the low ground along Grand Valley Rd falls into shadow while the shoulders above it are still in full sun.",
      consequence:
        "An acreage down on that low ground, booked against a Calgary sunset table, gets photographed in flat shade while the rises either side of it are still glowing.",
      resolution:
        "The shoot is scheduled to the valley's own light — floor properties flown earlier, rim properties held for the last sun — and the golden-hour set is delivered in the standard turnaround.",
    },
    landmarks: [
      "Bottrel Rd",
      "Grand Valley Rd",
      "Bottrel Community Hall",
      "Grand Valley",
      "Horse Creek",
    ],
    terrain:
      "Rolling foothill ranch country cut by Grand Valley and the Horse Creek drainage — open pasture on the rises, willow and poplar in the low lines, and the survey grid bending wherever the valleys refuse it.",
    windNote:
      "Chinook belt. West wind arrives off the foothills with the gust spread already built in, and Grand Valley carries enough relief to bend and funnel it — conditions on the valley floor can differ from the forecast on the rise.",
    seasonalWindow:
      "Snow holds in the valley bottoms and shaded draws after the open rises have cleared. Long summer evenings favour the upland; the valley floor gives up its direct light first, so the working golden hour is shorter down low than the almanac suggests.",
    bylawNote:
      "Rocky View County's rules govern takeoff from public road allowances and municipal land, and Big Hill Springs Provincial Park is not used as a launch site; the default is staging from the client's own property with consent.",
    subjectMix: ["acreage-estate", "agricultural", "luxury-residential"],
    mobilisationNote:
      "About 50 minutes northwest of the Calgary base. A comfortable half-day booking, and Cremona or Water Valley work can ride the same mobilisation.",
    faqs: [
      {
        question: "Does a drone flight at Bottrel need airspace authorisation?",
        answer:
          "No. Bottrel sits in uncontrolled Class G airspace, outside the charted Calgary-area control zones, with Springbank Airport about 36 km away. No authorisation is normally required to fly here. The exact site and altitude are still confirmed against NAV Drone before every flight, as standard practice.",
      },
      {
        question: "When should an acreage in Grand Valley be photographed?",
        answer:
          "Earlier than the sunset table says, if the property sits on the valley floor. Direct evening light leaves the low ground along Grand Valley Rd before it leaves the rises, so floor properties are planned to that earlier cutoff while rim properties can hold for the last sun.",
      },
    ],
    primaryKeywords: [
      "drone photography Bottrel",
      "aerial photography Bottrel",
      "Bottrel drone services",
    ],
    nearestLocations: ["cremona", "water-valley", "cochrane", "jumping-pound"],
  },

  // ───────────────────────────────────────────────────────── JUMPING POUND ──
  {
    slug: "jumping-pound",
    name: "Jumping Pound",
    region: "cochrane-west",
    city: "Rocky View County",
    province: "Alberta",
    coordinates: { lat: 51.0333, lng: -114.7833 },
    tier: 4,
    driveMinutesFromCalgary: 50,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 30,
      authorizationRequired: false,
      authorizationNote:
        "Jumping Pound lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 30 km from Springbank Airport (CYBW). No airspace authorisation is normally required. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "From altitude the built form at Jumping Pound is the ranch yard: house, barn, shelterbelt and corrals gathered into one tight cluster, repeated along Jumping Pound Rd and Hwy 68 with open grazing land between neighbours.",
      consequence:
        "A ground camera can photograph the buildings or the land, never the relationship between them — and out here the relationship is the value: how much country a yard commands and how the creek and roads reach it.",
      resolution:
        "A wide boundary-line orbit, flown high enough to hold the whole holding — yard at centre, fence lines running to the frame edges, Jumping Pound Creek where it crosses — delivered in the standard turnaround.",
    },
    landmarks: [
      "Jumping Pound Rd",
      "Hwy 68",
      "Jumping Pound Creek",
      "Sibbald Flat",
      "Jumping Pound Demonstration Forest",
    ],
    terrain:
      "Front-range foothill ranch country — high rolling grass broken by aspen bluffs, Jumping Pound Creek cutting through it, and managed forest rising west toward Sibbald Flat and the Powderface Trail trailhead.",
    windNote:
      "This is the first open ground east of the front ranges, and chinook wind crosses it at full strength — gusty, dry, and often strongest in exactly the clear weather that looks best from the air.",
    seasonalWindow:
      "Grass country carries colour longer than grain: the hills hold green into midsummer and cure to gold rather than stubble. Low autumn sun rakes across the ridgelines and makes the relief legible; midwinter flattens it under snow.",
    bylawNote:
      "Rocky View County's rules govern takeoff from public land here, and the Jumping Pound Demonstration Forest is not treated as a launch site; the working default is staging from the client's own property with consent.",
    subjectMix: ["acreage-estate", "agricultural", "luxury-residential"],
    mobilisationNote:
      "Roughly 50 minutes from the Calgary base. An easy half-day, and Springbank or Bragg Creek bookings can share the same run west.",
    faqs: [
      {
        question: "Is Jumping Pound affected by the Springbank Airport control zone?",
        answer:
          "No — the district sits in uncontrolled Class G airspace about 30 km from Springbank Airport (CYBW), outside the charted control zone, so no airspace authorisation is normally required. Because traffic thickens toward Springbank on the corridor east, the exact site and altitude are confirmed against NAV Drone before every flight.",
      },
      {
        question: "Can one flight show an entire ranch holding at Jumping Pound?",
        answer:
          "Usually, yes. Within the legal altitude limit, an orbit flown wide of the yard can hold the buildings, the fence lines and Jumping Pound Creek in a single frame. Larger holdings are covered as a short series of overlapping frames along the boundary rather than one impossible wide shot.",
      },
    ],
    primaryKeywords: [
      "drone photography Jumping Pound",
      "aerial photography Jumping Pound",
      "Jumping Pound drone services",
    ],
    nearestLocations: ["bottrel", "springbank", "bragg-creek", "cochrane"],
  },

  // ───────────────────────────────────────────────────────────── WAIPAROUS ──
  {
    slug: "waiparous",
    name: "Waiparous",
    region: "cochrane-west",
    city: "Bighorn No. 8 M.D.",
    province: "Alberta",
    coordinates: { lat: 51.2833, lng: -115.0833 },
    tier: 4,
    driveMinutesFromCalgary: 65,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Canmore municipal heliport",
      distanceToAerodromeKm: 28,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 28 km from Canmore municipal heliport, Waiparous lies outside the charted Calgary-area control zones, and no authorisation is normally required. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Waiparous barely shows itself from the ground — the village is cabin rooflines scattered under conifer canopy along Waiparous Creek, laid out to the creek's line rather than any grid, with Old Baldy Mountain standing behind the trees.",
      consequence:
        "At street level the forest wins: a camera on Waiparous Village Rd sees the timber and one cabin at a time, and the setting a buyer is actually paying for — creek, forest, mountain — never lands in a single frame.",
      resolution:
        "A rising reveal from the cabin's own clearing up through the canopy line assembles all three — roofline in the foreground, the creek's cut through the timber, Old Baldy behind — and the finished set is delivered in the standard turnaround.",
    },
    landmarks: [
      "Waiparous Village Rd",
      "Waiparous Creek",
      "Old Baldy Mountain",
      "Ghost River Rd",
      "Big Horn Campground",
    ],
    terrain:
      "Forested mountain-front country where the foothills end — cabin lots cut into pine and spruce along Waiparous Creek, the Ghost River country rising immediately west, and bare rock beginning at Old Baldy Mountain.",
    windNote:
      "Mountain-front air. Chinook wind descends here before it reaches the open prairie, and the Ghost and Waiparous valleys funnel it — the timber softens surface gusts inside the village while the air above the canopy runs hard.",
    seasonalWindow:
      "The peaks pull shadow across the village early on short days, and snow stays in the timber long after the open country east has cleared. Midsummer gives the longest usable evenings, and the conifer canopy keeps the frames green in every season.",
    bylawNote:
      "Municipal rules govern takeoff and landing on the village's roads and public land, and the Ghost River Wilderness Area is never used as a launch site; the default is staging from the client's own property with consent.",
    subjectMix: ["luxury-residential", "acreage-estate"],
    mobilisationNote:
      "About 65 minutes from the Calgary base — the far end of the same run that serves Ghost Lake and CottageClub, so bookings out here pair naturally with either. Best planned as a half-day.",
    faqs: [
      {
        question: "Do you need authorisation to fly a drone at Waiparous?",
        answer:
          "No. Waiparous sits in uncontrolled Class G airspace, well outside the charted Calgary-area control zones, with the nearest aerodrome the Canmore municipal heliport about 28 km away. No airspace authorisation is normally required. The specific site and altitude are still confirmed against NAV Drone before every flight.",
      },
      {
        question: "Can aerial photos at Waiparous include the Ghost River Wilderness Area?",
        answer:
          "As a backdrop, yes. The flight itself stays over the village and the client's property — the wilderness area is not overflown or used as a launch site — but from altitude above Waiparous the camera can hold the Ghost country and Old Baldy Mountain as the horizon behind a cabin.",
      },
    ],
    primaryKeywords: [
      "drone photography Waiparous",
      "aerial photography Waiparous",
      "Waiparous drone services",
    ],
    nearestLocations: ["ghost-lake", "cottageclub", "cremona", "morley"],
  }
];
